/* eslint max-lines-per-function: ["error", {max: 193}] -- applies to entire file */
/* eslint max-statements: ["error", { max: 25 }] -- applies to entire file */

import { useState } from "react";

import { format, isFuture } from "date-fns";
import capitalize from "lodash/capitalize";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

import {
  AnalyticsComponent,
  AnalyticsFieldName,
  SMTEntryAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { DateFormat } from "@/constants/DateFormat";
import { SMTTestID } from "@/constants/TestID";
import { useToast } from "@/hooks/useToast";
import { useAnalytics } from "@/services/Analytics";
import type { AddMealRequestBody } from "@/store/api/meals";
import { useAppSelector } from "@/store/hooks";
import { selectSMTPhoto, useSMTDispatch } from "@/store/reducers/smt";
import type { SMTScreenPath } from "@/ui/components/smt/AddSMTEntry/AddSMTEntryFlow";
import AddFoodTypeButtonBar from "@/ui/components/smt/food/AddFoodTypeButtonBar";
import DateTimePicker from "@/ui/composed/pickers/DateTimePicker";
import { Button, FAIconName, Image, TextArea } from "@/ui/core";
import { Box, HStack, ScrollView, VStack } from "@/ui/layout";
import { joinTestIDs } from "@/utils/string.util";

const baseTestID = SMTTestID.AddFood;

/**
 * This is the container for the add food modal.
 *
 * It serves as a higher level component to maintain the photo selection
 * And form state for the "Add meal" SMT entry flow.
 *
 * Will be expanding this in https://teamequip.atlassian.net/browse/JN-1394
 */
interface AddFoodContainerProps {
  onSaveEntry: (params: AddMealRequestBody) => Promise<void>;
  patientExternalId: string;
  screenPath: SMTScreenPath;
  uiConfig: SMTEntryFormUIConfig;
}

const getAnalyticsFoodType = (foodType: SMTFoodType | undefined) => {
  return foodType ? capitalize(foodType) : "Not Selected";
};

const AddFoodContainer = ({
  onSaveEntry,
  patientExternalId,
  screenPath,
  uiConfig,
}: AddFoodContainerProps) => {
  const { showToast } = useToast();
  const [date, setDate] = useState<Date>(() => new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFoodType, setSelectedFoodType] = useState<
    SMTFoodType | undefined
  >(undefined);
  const [isNewFoodChecked, setIsNewFoodChecked] = useState<boolean>(false);
  const [foodComment, setFoodComment] = useState<string>("");

  // Only relevant when no photo is present
  const [foodDescription, setFoodDescription] = useState<string>("");
  const { track } = useAnalytics();

  const { setIsFilePickerModalOpen } = useSMTDispatch();
  const photo = useAppSelector(selectSMTPhoto);
  const hasPhoto = !isNil(photo);
  const isFoodCommentProvided = !isEmpty(foodComment.trim());
  const isFoodDescriptionProvided = !isEmpty(foodDescription.trim());

  const handleSaveEntry = async () => {
    if (isLoading) return;
    setIsLoading(true);

    trackFoodEntrySubmission();

    await onSaveEntry({
      comment: foodComment.trim(),
      description: hasPhoto ? undefined : foodDescription.trim(),
      isNewFood: isNewFoodChecked,
      mealType: selectedFoodType,
      occurredAt: format(date, DateFormat.dateTimeWithoutTimezone),
      patientExternalId,
    });

    setIsLoading(false);
  };

  const trackFoodEntrySubmission = () => {
    track(SMTEntryAnalyticsEvent.SaveEntryPressed, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.Button,
      [AnalyticsFieldName.EntryDate]: date.toISOString(),
      [AnalyticsFieldName.FoodCommentProvided]: isFoodCommentProvided,
      [AnalyticsFieldName.FoodDescriptionProvided]: hasPhoto
        ? undefined
        : isFoodDescriptionProvided,
      [AnalyticsFieldName.HasPhoto]: hasPhoto,
      [AnalyticsFieldName.IsNewFood]: isNewFoodChecked,
      [AnalyticsFieldName.MealType]: getAnalyticsFoodType(selectedFoodType),
      [AnalyticsFieldName.Source]: screenPath,
    });
  };

  const handleDateChange = (newDate: Date) => {
    if (isFuture(newDate)) {
      showToast({
        text: "The date you selected is in the future. Please choose a date & time that is now or earlier.",
        variant: "error",
      });
      return;
    }
    setDate(newDate);
  };

  const handleFoodTypePress = (foodType: SMTFoodType) => {
    if (selectedFoodType === foodType) {
      return;
    }
    setSelectedFoodType(foodType);
    track(SMTEntryAnalyticsEvent.FoodTypeSelected, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.Button,
      [AnalyticsFieldName.NewValue]: getAnalyticsFoodType(foodType),
      [AnalyticsFieldName.Source]: screenPath,
    });
  };

  const handleNewFoodPress = () => {
    const isChecked = !isNewFoodChecked;
    setIsNewFoodChecked(isChecked);
    track(SMTEntryAnalyticsEvent.NewFoodPressed, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.Button,
      [AnalyticsFieldName.NewValue]: isChecked,
      [AnalyticsFieldName.Source]: screenPath,
    });
  };

  const handleAddImage = ({
    isReplacingImage = false,
  }: {
    isReplacingImage?: boolean;
  } = {}) => {
    setIsFilePickerModalOpen(true);

    const event = isReplacingImage
      ? SMTEntryAnalyticsEvent.ReplaceImagePressed
      : SMTEntryAnalyticsEvent.AddPhotoPressed;

    const eventComponent = isReplacingImage
      ? AnalyticsComponent.Button
      : AnalyticsComponent.Card;

    track(event, {
      [AnalyticsFieldName.Component]: eventComponent,
      [AnalyticsFieldName.Source]: screenPath,
    });
  };

  const isSaveButtonEnabled = hasPhoto || isFoodDescriptionProvided;

  return (
    <ScrollView
      keyboardDismissMode="on-drag"
      keyboardShouldPersistTaps="handled"
    >
      <VStack gap={18} paddingTop={12}>
        {hasPhoto ? (
          <Box position="relative">
            <Image
              borderRadius={12}
              contentFit="cover"
              contentType={photo.mimeType}
              size={{ height: 180, width: "100%" }}
              testID={baseTestID}
              uriSource={photo.uri}
            />
            <Box alignSelf="center" bottom={18} position="absolute">
              <Button
                isDisabled={isLoading}
                leftIcon={FAIconName.Redo}
                onPress={() => handleAddImage({ isReplacingImage: true })}
                size="small"
                testID={joinTestIDs(baseTestID, "replace-image")}
                title={uiConfig.buttons.replaceImage}
              />
            </Box>
          </Box>
        ) : (
          <TextArea
            isBottomSheetTextArea
            onChange={setFoodDescription}
            placeholder={uiConfig.placeholderText.description}
            testID={joinTestIDs(baseTestID, SMTTestID.AddFoodDescription)}
            value={foodDescription}
          />
        )}
        <AddFoodTypeButtonBar
          isNewFoodChecked={isNewFoodChecked}
          onFoodTypePress={handleFoodTypePress}
          onNewFoodPress={handleNewFoodPress}
          selectedFoodType={selectedFoodType}
        />
        <TextArea
          isBottomSheetTextArea
          onChange={setFoodComment}
          placeholder={uiConfig.placeholderText.comment}
          testID={joinTestIDs(baseTestID, SMTTestID.AddFoodComment)}
          value={foodComment}
        />
        {!hasPhoto && (
          <Button
            leftIcon={FAIconName.Camera}
            onPress={() => handleAddImage()}
            testID={joinTestIDs(baseTestID, SMTTestID.AddPhoto)}
            title={uiConfig.buttons.addPhoto}
            variant="secondary"
          />
        )}
        <HStack alignItems="center" justifyContent="space-between">
          <DateTimePicker
            date={date}
            isCurrentDateMaxDate
            mode="datetime"
            onChange={handleDateChange}
            testID={joinTestIDs(baseTestID, "date-time-picker")}
          />
          <Button
            isDisabled={!isSaveButtonEnabled || isLoading}
            isLoading={isLoading}
            leftIcon={FAIconName.Check}
            onPress={handleSaveEntry}
            testID={joinTestIDs(baseTestID, SMTTestID.SaveEntry)}
            title={uiConfig.buttons.saveEntry}
          />
        </HStack>
      </VStack>
    </ScrollView>
  );
};

export type { AddFoodContainerProps };
export default AddFoodContainer;
